import type { Coordinates, Place, PlaceKind, Workspace, WorkspaceId } from '../domain/types.ts';
import { I10_OBSERVATIONS } from './routes.ts';
export const WORKSPACES: readonly Workspace[] = [
  { id: 'personal', organizationId: 'org:personal', name: 'Personal', timeZone: 'America/Los_Angeles', tracksolidAccount: 'latisent_personal' },
  { id: 'coastline', organizationId: 'org:coastline', name: 'Coastline Auto Logistics', timeZone: 'America/Phoenix', tracksolidAccount: 'latisent_coastline' },
  { id: 'ridgeline', organizationId: 'org:ridgeline', name: 'Ridgeline Builders', timeZone: 'America/Phoenix', tracksolidAccount: 'latisent_ridgeline' },
  { id: 'northstar', organizationId: 'org:northstar', name: 'Northstar Freight', timeZone: 'America/Chicago', tracksolidAccount: 'latisent_northstar' },
];
export const STORY_IDS = {
  wallet: 'personal:asset:0', vin4417: 'coastline:asset:0', vin4417Shipment: 'coastline:shipment:4417',
  unit0417: 'ridgeline:asset:0', yardB: 'ridgeline:place:yard', unseenPallet: 'northstar:asset:0',
} as const;
export function placesFor(workspaceId: WorkspaceId): Place[] {
  const places: Place[] = [];
  const add = (key: string, name: string, kind: PlaceKind, lat: number, lng: number, radiusM = 250, timeZone = 'America/Phoenix') => {
    places.push({ id: `${workspaceId}:place:${key}`, workspaceId, name, kind,
      geometry: { type: 'circle', center: { lat, lng }, radiusM }, timeZone, vendorFenceId: `${workspaceId}:fence:${key}` });
  };
  if (workspaceId === 'personal') {
    add('home', 'Home', 'home', 37.75021, -122.43372, 200, 'America/Los_Angeles');
    add('office', 'SoMa office', 'office', 37.78655, -122.39668, 200, 'America/Los_Angeles');
    add('park', 'Dolores Park', 'park', 37.75962, -122.4269, 200, 'America/Los_Angeles');
    add('sfo', 'San Francisco International', 'airport', 37.6152, -122.3899, 700, 'America/Los_Angeles');
    add('sea', 'Seattle-Tacoma International', 'airport', 47.4502, -122.3088, 700, 'America/Los_Angeles');
    add('seattle', 'Capitol Hill hotel', 'hotel', 47.6143, -122.3194, 250, 'America/Los_Angeles');
    add('cafe', 'Sightglass Coffee, SoMa', 'cafe', 37.77696, -122.40842, 200, 'America/Los_Angeles');
    add('gym', 'Mission Cliffs', 'gym', 37.7567, -122.4121, 200, 'America/Los_Angeles');
    add('groceries', 'Noe Valley market', 'store', 37.7513, -122.4315, 200, 'America/Los_Angeles');
    add('weekend', 'Crissy Field', 'park', 37.8039, -122.4658, 250, 'America/Los_Angeles');
  } else if (workspaceId === 'coastline') {
    const names = ['Port of Long Beach', 'Ontario', 'Indio fuel stop', 'Blythe, Arizona crossing', 'Tempe dealership'];
    const kinds: PlaceKind[] = ['yard', 'checkpoint', 'checkpoint', 'checkpoint', 'dealership'];
    I10_OBSERVATIONS.filter(p => 'checkpoint' in p).forEach((p, i) => add(String(i), names[i], kinds[i], p.lat, p.lng));
    add('tucson', 'Tucson dealership', 'dealership', 32.2204, -110.9682);
    add('riverside', 'Riverside staging yard', 'yard', 33.9952, -117.3735);
    add('portla', 'Port of Los Angeles', 'yard', 33.7462, -118.2702, 450, 'America/Los_Angeles');
    add('hueneme', 'Port Hueneme', 'yard', 34.1482, -119.2049, 450, 'America/Los_Angeles');
    add('oakland', 'Port of Oakland', 'yard', 37.7955, -122.2994, 450, 'America/Los_Angeles');
    add('phoenix', 'Camelback Motors, Phoenix', 'dealership', 33.5092, -112.0477);
    add('scottsdale', 'Scottsdale Auto Center', 'dealership', 33.4628, -111.9244);
    add('vegas', 'Desert Valley Motors, Las Vegas', 'dealership', 36.069, -115.174, 300, 'America/Los_Angeles');
    add('albuquerque', 'Rio Grande Auto, Albuquerque', 'dealership', 35.1265, -106.6003, 300, 'America/Denver');
    add('sandiego', 'Mission Valley Motors, San Diego', 'dealership', 32.7667, -117.1544, 300, 'America/Los_Angeles');
    add('palmsprings', 'Palm Springs Auto Mall', 'dealership', 33.7995, -116.4968, 300, 'America/Los_Angeles');
  } else if (workspaceId === 'ridgeline') {
    add('yard', 'Yard B', 'yard', 33.4336, -112.1537, 300);
    add('mesa', 'Mesa civic library', 'job_site', 33.4152, -111.8315);
    add('tempe', 'Tempe science building', 'job_site', 33.4172, -111.9390);
    add('glendale', 'Glendale medical pavilion', 'job_site', 33.5387, -112.1855);
    places[0].geometry = { type: 'polygon', vertices: [
      { lat: 33.4316, lng: -112.157 }, { lat: 33.4356, lng: -112.157 },
      { lat: 33.4356, lng: -112.1504 }, { lat: 33.4316, lng: -112.1504 },
    ] };
  } else {
    add('phoenix', 'Phoenix warehouse', 'warehouse', 33.4336, -112.1537);
    add('dallas', 'Dallas distribution center', 'warehouse', 32.7666, -96.9041, 400, 'America/Chicago');
    add('la', 'Los Angeles cross-dock', 'warehouse', 33.9924, -118.2226, 400, 'America/Los_Angeles');
    add('elpaso', 'El Paso cross-dock', 'warehouse', 31.7735, -106.3712, 350, 'America/Denver');
    add('tucson', 'Tucson distribution hub', 'warehouse', 32.139, -110.946);
    add('vegas', 'Las Vegas fulfillment center', 'warehouse', 36.2365, -115.098, 350, 'America/Los_Angeles');
    add('lascruces', 'Las Cruces checkpoint', 'checkpoint', 32.286, -106.766, 250, 'America/Denver');
  }
  return places;
}
export function center(place: Place): Coordinates {
  if (place.geometry.type === 'circle') return place.geometry.center;
  const vs = place.geometry.vertices;
  return { lat: vs.reduce((n, p) => n + p.lat, 0) / vs.length, lng: vs.reduce((n, p) => n + p.lng, 0) / vs.length };
}
